import React, { useMemo, useState } from "react";
import { Settings } from "lucide-react";
import { useSimulator } from "../Simulator/SimulatorStore";

// ----- Configuration metadata (easily swapped with API data) -----
type GlobalFieldKey = "dailyLeadVolume" | "leadQuality" | "baseConversionRate" | "cycleTimeImpact";
interface GlobalFieldMeta {
  key: GlobalFieldKey;
  label: string;
  info?: string;
  min?: number;
  max?: number;
  step?: number;
}
const GLOBAL_FIELDS: GlobalFieldMeta[] = [
  { key: "dailyLeadVolume", label: "Daily Lead Volume", info: "Number of new leads entering the system each day", min: 0 },
  { key: "leadQuality", label: "Lead Quality (0-100)", info: "Composite measure of lead quality including: intent signals, lead freshness, exclusivity, demographic match to ideal customer profile, and completeness of contact information. Higher scores indicate leads with stronger conversion potential.", min: 0, max: 100 },
  { key: "baseConversionRate", label: "Base Conversion Rate (%)", info: "Starting conversion rate before any adjustments for quality, cycle time, or process efficiency", min: 0, max: 100 },
  { key: "cycleTimeImpact", label: "Cycle Time Impact (%/day)", info: "Conversion rate penalty per day beyond 5 days. Higher values = customers lose interest faster.", min: 0, max: 100 },
];

interface GlobalValues {
  dailyLeadVolume: number;
  leadQuality: number;
  baseConversionRate: number;
  cycleTimeImpact: number;
}
const GLOBAL_DEFAULTS: GlobalValues = {
  dailyLeadVolume: 13,
  leadQuality: 75,
  baseConversionRate: 50,
  cycleTimeImpact: 8,
};

interface ProcessRow {
  name: string;
  capacityPerPerson: number;
  simpleTime: number;
  complexTime: number;
  complexityPct: number;
  baseReworkPct: number;
  experienceImpact: number;
  automationImpact: number;
  maturityImpact: number;
}
const PROCESS_DEFAULTS: ProcessRow[] = [
  { name: "Contact Management", capacityPerPerson: 8, simpleTime: 0.8, complexTime: 1.5, complexityPct: 60, baseReworkPct: 25, experienceImpact: 50, automationImpact: 50, maturityImpact: 30 },
  { name: "Pipeline Management", capacityPerPerson: 8, simpleTime: 0.25, complexTime: 0.55, complexityPct: 50, baseReworkPct: 30, experienceImpact: 50, automationImpact: 50, maturityImpact: 30 },
  { name: "Document Review", capacityPerPerson: 8, simpleTime: 0.3, complexTime: 0.65, complexityPct: 70, baseReworkPct: 40, experienceImpact: 50, automationImpact: 50, maturityImpact: 30 },
  { name: "Quoting", capacityPerPerson: 8, simpleTime: 0.6, complexTime: 1.8, complexityPct: 60, baseReworkPct: 35, experienceImpact: 50, automationImpact: 50, maturityImpact: 30 },
  { name: "Compliance", capacityPerPerson: 8, simpleTime: 0.3, complexTime: 1, complexityPct: 40
    , baseReworkPct: 20, experienceImpact: 50, automationImpact: 50, maturityImpact: 30 },
];

// Utility to update a process row immutably
function updateProcess(processes: ProcessRow[], idx: number, patch: Partial<ProcessRow>): ProcessRow[] {
  const copy = [...processes];
  copy[idx] = { ...copy[idx], ...patch };
  return copy;
}

export default function SystemSettings() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { global, setGlobal, processes, updateProcessBase } = useSimulator() as any;

  const settingsContainerMaxHeight = useMemo(() => (settingsOpen ? "max-h-[5000px]" : "max-h-0"), [settingsOpen]);

  function clampValue(val: number, min?: number, max?: number) {
    if (min !== undefined && val < min) return min;
    if (max !== undefined && val > max) return max;
    return val;
  }

  function handleGlobalChange(meta: GlobalFieldMeta, raw: string) {
    const num = Number(raw);
    if (Number.isNaN(num)) return;
    const stepped = meta.step ? Math.round(num / meta.step) * meta.step : num;
    const clamped = clampValue(stepped, meta.min, meta.max);
    setGlobal({ [meta.key]: clamped } as any);
  }

  return (
    <div className="relative">
      {/* Desktop Header with System Settings */}
      <div className="hidden md:flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Momentum Process Simulator</h1>
          <p className="text-gray-500">Lead to Policy Conversion Analysis</p>
        </div>
        <button
          onClick={() => setSettingsOpen((v) => !v)}
          className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm hover:bg-gray-50 text-gray-700"
        >
          <Settings className="w-5 h-5 text-gray-700" />
          {"System Settings"}
        </button>
      </div>
      {/* Mobile quick actions */}
      <div className="flex items-center justify-end gap-3 mt-3 md:hidden">
        <button
          onClick={() => setSettingsOpen((v) => !v)}
          className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm hover:bg-gray-50 text-gray-700"
          aria-label="Toggle System Settings"
        >
          <Settings className="w-5 h-5 text-gray-700" />
        </button>
      </div>
            <section
        className={`overflow-hidden transition-[max-height] duration-300 ${settingsContainerMaxHeight}`}
        aria-expanded={settingsOpen}
      >
        <div className="mt-4 bg-white border border-gray-200 shadow-sm rounded-xl p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">System Configuration</h2>

          {/* Global Settings */}
          <div className="mb-6">
            <h3 className="font-medium text-gray-700 mb-3">Global Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 gap-y-5">
              {GLOBAL_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="block text-sm text-gray-700 mb-1 font-medium">
                    <span>{f.label}</span>
                    {f.info && (
                      <div className="relative ml-1 inline-block align-middle group">
                        <div
                          className="w-3 h-3 rounded-full border border-gray-400 flex items-center justify-center text-xs text-gray-600 cursor-help"
                          style={{ fontSize: "8px" }}
                          aria-label={f.info}
                        >
                          i
                        </div>
                        <div className="absolute bottom-5 left-0 transform translate-x-0 bg-gray-800 text-white text-xs rounded py-2 px-3 opacity-0 group-hover:opacity-100 transition-opacity z-10 w-64 whitespace-normal text-left">
                          {f.info}
                        </div>
                      </div>
                    )}
                  </label>
                  <input
                    type="number"
                    inputMode="numeric"
                    className="w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 px-3 h-9 text-sm"
                    value={global[f.key]}
                    onChange={(e) => handleGlobalChange(f, e.target.value)}
                    min={f.min ?? 0}
                    {...(f.max !== undefined ? { max: f.max } : {})}
                    step={f.step ?? 1}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Process Configuration */}
          <div>
            <h3 className="font-medium text-gray-800 mb-3">Process Configuration</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate [border-spacing:0]">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Process</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Capacity/Person (hrs/day)</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Simple Time (hrs)</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Complex Time (hrs)</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Complexity %</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Base Rework %</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Experience Impact %</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Automation Impact %</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Maturity Impact %</th>
                  </tr>
                </thead>
                <tbody>
                  {processes.map((p: any, idx: number) => (
                    <tr key={p.base.name} className="bg-white">
                      <td className="border-y border-gray-200 px-3 py-2 text-sm text-gray-800">{p.base.name}</td>
                      <td className="border-y border-gray-200 px-3 py-2">
                        <input type="number" value={p.base.capacityPerPerson} min={0} step={0.5}
                          onChange={(e) => updateProcessBase(idx, { capacityPerPerson: Number(e.target.value) })}
                          className="w-20 md:w-24 rounded-md border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </td>
                      <td className="border-y border-gray-200 px-3 py-2">
                        <input type="number" value={p.base.simpleTime} min={0} step={0.05}
                          onChange={(e) => updateProcessBase(idx, { simpleTime: Number(e.target.value) })}
                          className="w-20 md:w-24 rounded-md border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </td>
                      <td className="border-y border-gray-200 px-3 py-2">
                        <input type="number" value={p.base.complexTime} min={0} step={0.05}
                          onChange={(e) => updateProcessBase(idx, { complexTime: Number(e.target.value) })}
                          className="w-20 md:w-24 rounded-md border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </td>
                      <td className="border-y border-gray-200 px-3 py-2">
                        <input type="number" value={p.base.complexityPct} min={0} max={100}
                          onChange={(e) => updateProcessBase(idx, { complexityPct: Number(e.target.value) })}
                          className="w-20 md:w-24 rounded-md border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </td>
                      <td className="border-y border-gray-200 px-3 py-2">
                        <input type="number" value={p.base.baseReworkPct} min={0} max={100}
                          onChange={(e) => updateProcessBase(idx, { baseReworkPct: Number(e.target.value) })}
                          className="w-20 md:w-24 rounded-md border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </td>
                      <td className="border-y border-gray-200 px-3 py-2">
                        <input type="number" value={p.base.experienceImpact} min={0} max={100}
                          onChange={(e) => updateProcessBase(idx, { experienceImpact: Number(e.target.value) })}
                          className="w-20 md:w-24 rounded-md border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </td>
                      <td className="border-y border-gray-200 px-3 py-2">
                        <input type="number" value={p.base.automationImpact} min={0} max={100}
                          onChange={(e) => updateProcessBase(idx, { automationImpact: Number(e.target.value) })}
                          className="w-20 md:w-24 rounded-md border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </td>
                      <td className="border-y border-gray-200 px-3 py-2">
                        <input type="number" value={p.base.maturityImpact} min={0} max={100}
                          onChange={(e) => updateProcessBase(idx, { maturityImpact: Number(e.target.value) })}
                          className="w-20 md:w-24 rounded-md border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Guide */}
            <div className="mt-4 p-3 bg-gray-50 rounded text-xs text-gray-700">
              <h5 className="font-semibold mb-2">Configuration Guide:</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p><strong>Capacity Settings:</strong></p>
                  <ul className="ml-4 list-disc">
                    <li><strong>Capacity/Person:</strong> Work hours available per person per day (typically 6-8)</li>
                    <li><strong>Simple/Complex Time:</strong> Hours needed to process each case type</li>
                    <li><strong>Complexity %:</strong> Percentage of cases that are complex vs simple</li>
                    <li><strong>Base Rework %:</strong> Default percentage needing rework (reduced by maturity)</li>
                  </ul>
                </div>
                <div>
                  <p><strong>Impact Settings (how much each factor improves performance):</strong></p>
                  <ul className="ml-4 list-disc">
                    <li><strong>Experience Impact:</strong> Time reduction from junior (0%) to expert (100%)</li>
                    <li><strong>Automation Impact:</strong> Time reduction from manual (0%) to fully automated (100%)</li>
                    <li><strong>Maturity Impact:</strong> Quality improvement & rework reduction at full maturity</li>
                  </ul>
                </div>
              </div>
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                <p className="font-semibold">💡 Key Formulas:</p>
                <p className="mt-1">• <strong>Effective Time</strong> = Base Time × (1 - Experience%) × (1 - Automation%)</p>
                <p>• <strong>Daily Throughput</strong> = (Capacity × Staff) ÷ Effective Time</p>
                <p>• <strong>Effective Rework</strong> = Base Rework × (1 - Maturity Impact)</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
